/**
 * useTask — fetch a single cleaning_task with its room and checklist items.
 *
 * Returns three things in one query result:
 *   task   — the cleaning_task row with joined room
 *   items  — checklist items for the task, in display order
 *
 * Photo uploads / requirements are handled in a separate hook in Day 2 to
 * keep this file under the 600-line limit and the responsibilities clean.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export interface TaskRow {
  id:                  string
  room_id:             string
  status:              string
  cycle_type:          string
  started_at:          string | null
  completed_at:        string | null
  assigned_to_user_id: string | null
  // PROJECT_STATE task #73 — partial-unit scope. is_partial_unit
  // drives the chip; kitchen_excluded is meaningful only when
  // is_partial_unit=true; partial_scope_description is a free-form
  // note (≤500 chars) shown inline on the task detail page.
  is_partial_unit:           boolean
  kitchen_excluded:          boolean
  partial_scope_description: string | null
  // PROJECT_STATE task #74a — operational counters recorded by the
  // cleaner at submit time. Both bounded 0–10 by CHECK + RPC; columns
  // are NOT NULL DEFAULT 0 so every row has a defined value.
  trash_bag_count:           number
  appliances_cleaned_count:  number
  room: {
    id:             string
    room_number:    string
    current_status: string
    building_id:    string
    floor_id:       string
    room_type_id:   string | null
    // Joined names for the LocationLine on TaskDetailPage. Same
    // shape as PendingInspectionRow.room so the multi-campus
    // operator can tell which campus they're looking at without
    // having to navigate to the room board.
    campus_id:      string | null
    campus:   { id: string; name: string } | null
    building: { id: string; name: string } | null
    floor:    { id: string; label: string } | null
  } | null
}

export interface ChecklistItemRow {
  id:                     string
  cleaning_task_id:       string
  template_item_id:       string | null
  label:                  string
  is_required:            boolean
  completed:              boolean
  completed_at:           string | null
  // PROJECT_STATE task #72 — N/A escape hatch for "(if applicable)"
  // items the cleaner cannot complete because the relevant fixture
  // is not present (no recycling bin, no vent-a-hood, etc.). The
  // readiness guard in check_room_ready_preconditions treats
  // is_not_applicable=true the same as completed=true.
  is_not_applicable:      boolean
  not_applicable_reason:  string | null
}

export interface TaskBundle {
  task:  TaskRow
  items: ChecklistItemRow[]
}

export function useTask(taskId: string | undefined): UseQueryResult<TaskBundle> {
  const supabase = useSupabase()

  return useQuery({
    queryKey: ['task', taskId],
    enabled:  !!taskId,
    queryFn: async (): Promise<TaskBundle> => {
      if (!taskId) throw new Error('useTask: taskId is required')

      const [taskRes, itemsRes] = await Promise.all([
        supabase
          .from('cleaning_tasks')
          .select(
            'id, room_id, status, cycle_type, started_at, completed_at, assigned_to_user_id,' +
            ' is_partial_unit, kitchen_excluded, partial_scope_description,' +
            ' trash_bag_count, appliances_cleaned_count,' +
            ' room:rooms(id, room_number, current_status, building_id, floor_id, room_type_id, campus_id,' +
            '   campus:campuses(id, name),' +
            '   building:buildings(id, name),' +
            '   floor:floors(id, label))',
          )
          .eq('id', taskId)
          .single(),
        supabase
          .from('cleaning_checklist_items')
          // label is aliased from item_label so consumers can read .label;
          // the underlying column has always been item_label since the
          // 20260501000002_schema migration. Order by created_at — items
          // are seeded from templates in sort_order, so insertion order
          // is the natural display order.
          .select('id, cleaning_task_id, template_item_id, label:item_label, is_required, completed, completed_at, is_not_applicable, not_applicable_reason')
          .eq('cleaning_task_id', taskId)
          .order('created_at', { ascending: true }),
      ])

      if (taskRes.error)  throw taskRes.error
      if (itemsRes.error) throw itemsRes.error

      return {
        task:  taskRes.data  as unknown as TaskRow,
        items: (itemsRes.data ?? []) as unknown as ChecklistItemRow[],
      }
    },
  })
}
